import type { HttpRequestError } from './errors';
import { attachCause, isHttpRequestError } from './errors';
import { streamOf } from './results';
import type {
  RequestOptions,
  RequestResult,
  Trace,
  UntracedResult,
} from './types';

/* Delivery. The outermost layer, and the only thing `throwOnError` decides. */

/**
 * The result rebuilt from the failure the pipeline threw.
 *
 * Everything a result carries about an answer is on the failure already -- that
 * is what the response branch of the vocabulary IS -- so this reshapes one value
 * rather than reading the response a second time. The two fallbacks are the
 * types being honest: both fields are optional on the branch, and a failure
 * built by hand may leave them out.
 */
function resultOfFailure(failure: HttpRequestError): UntracedResult<unknown> {
  return {
    body: failure.body,
    headers: failure.headers ?? new Headers(),
    ok: false,
    status: failure.status,
    // Belt and braces for a transport that lies: `HttpResponse` requires a
    // string here, so this only fires when a `fetch` broke the contract that
    // FE6 puts squarely on the caller. Deliberately untested: a test would have
    // to violate that contract on purpose to reach it.
    statusText: failure.statusText ?? '',
  };
}

/**
 * `isHttpRequestError`, made total, for the reason `oursToLabel` in
 * `request.ts` gives.
 *
 * A type predicate rather than a `boolean`, so the narrowing its caller had
 * before the guard survives it: without that, everything downstream sees
 * `unknown` again.
 */
function oursToDeliver(failure: unknown): failure is HttpRequestError {
  try {
    return isHttpRequestError(failure);
  } catch {
    return false;
  }
}

/**
 * Hands this caller the failure the way this caller asked for it.
 *
 * Delivery only. The failure was produced either way, which is what keeps one
 * failure channel instead of two, and it is the SAME object on every path that
 * throws: a caller branching on `instanceof NotFoundError` after a predicate
 * returned `true` finds what the default would have given them.
 *
 * ONLY STATUS FAILURES. A network error, a timeout and a caller abort do not
 * come past the first line, because the wording of the option is the promise: a
 * non-ok RESPONSE. Those three have no response, so there is nothing to hand
 * back in the failure's place (TE5).
 *
 * `rawFailure` is the result a raw call already had in hand when the status
 * turned out to be a failure, stream and all, and on a raw call it IS the
 * answer. Rebuilding one from the failure instead would deliver a value with no
 * `stream` on it, while the overload promised a `RawRequestResult` -- typed,
 * absent at runtime, the shape DECISION 3.6 exists to rule out. A decoded call
 * has nothing to hand over, so its result is rebuilt.
 *
 * A predicate that throws IS caught here. It was asked for an opinion about
 * this failure and gave a broken one instead of an answer, so the caller still
 * gets the failure it would have gotten under the default -- with the
 * predicate's own error attached as `cause`, via `attachCause`, the same
 * demotion a throwing `retry.shouldRetry` already gets (TE6, ER62).
 */
function delivered(
  failure: unknown,
  options: RequestOptions,
  rawFailure: UntracedResult<unknown> | undefined,
  trace: Trace,
): RequestResult<unknown> {
  // GUARDED, and the guard only makes the existing intent total. This line
  // already says "not one of ours, so hand it back untouched"; a duck-typed
  // read that throws would hand back the getter's error instead, taking the
  // same exit by the wrong door. MEASURED by BK6.
  if (!oursToDeliver(failure)) throw failure;

  const throwOnError = options.throwOnError ?? true;
  if (throwOnError === true) throw failure;

  // Settled before the predicate is asked, because both halves need it: the
  // predicate reads it to decide (DR10), and a `false` answer IS the delivery.
  // Traced BEFORE the predicate is asked, because `DR10` hands it the public
  // union and that is the shape the caller would be given. A predicate deciding
  // on a poorer result than the one it decides about would be a second shape.
  const result = { ...(rawFailure ?? resultOfFailure(failure)), trace };
  if (throwOnError === false) return result;
  let wantsThrow: boolean;
  try {
    wantsThrow = throwOnError(result);
  } catch (predicateError) {
    attachCause(failure, predicateError);
    throw failure;
  }
  if (wantsThrow) throw failure;
  return result;
}

/**
 * The delivery decision, plus the one act of cleanup only that decision can
 * authorise.
 *
 * A raw call's unread stream goes to the caller or to nobody, and `delivered` is
 * what decides which. This reads that decision by whether it THREW, rather than
 * asking `throwOnError` a second time: a second copy of the rule is a copy that
 * drifts, and the predicate form cannot be asked twice at all -- it is the
 * caller's own function, and a second call is a second side effect (TE6).
 *
 * Cancelling on the non-ok status instead, which is where this started, took the
 * stream away from exactly the callers who had asked for it (ER47).
 */
export async function deliverFailure(
  failure: unknown,
  options: RequestOptions,
  rawFailure: UntracedResult<unknown> | undefined,
  trace: Trace,
): Promise<RequestResult<unknown>> {
  try {
    return delivered(failure, options, rawFailure, trace);
  } catch (deliveryError) {
    // Nobody will read it now, and an abandoned stream holds its connection open
    // until it is collected. Past a handover it would have been the caller's to
    // close (RR9); this one never got that far (ER46).
    await streamOf(rawFailure)
      ?.cancel()
      .catch(() => {});
    throw deliveryError;
  }
}
